import React, { useCallback, useEffect, useMemo, useState } from "react";
import Section from "./Section";
import * as content from "../content";

export default function Gallery() {
  const { gallery: g } = content;
  const slides = g.imagePaths;
  const [index, setIndex] = useState(0);
  const count = slides.length;

  const go = useCallback(
    (delta) => {
      if (!count) return;
      setIndex((i) => (i + delta + count) % count);
    },
    [count]
  );

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowLeft") go(-1);
      if (e.key === "ArrowRight") go(1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [go]);

  const touchRef = React.useRef({ x: 0, y: 0 });

  const onTouchStart = (e) => {
    const t = e.changedTouches[0];
    touchRef.current = { x: t.clientX, y: t.clientY };
  };

  const onTouchEnd = (e) => {
    const t = e.changedTouches[0];
    const dx = t.clientX - touchRef.current.x;
    const dy = t.clientY - touchRef.current.y;
    if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
      go(dx < 0 ? 1 : -1);
    }
  };

  const current = useMemo(() => slides[index], [slides, index]);

  if (!count) return null;

  return (
    <Section id="gallery" className="section--gallery">
      <h2 className="section-title">{g.heading}</h2>
      <div
        className="carousel"
        role="region"
        aria-roledescription="carousel"
        aria-label={g.ariaLabel}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        <div className="carousel__viewport">
          <img
            key={current}
            className="carousel__image"
            src={current}
            alt={`${g.slideLabel} ${index + 1} of ${count}`}
            width={900}
            height={1350}
            loading={index === 0 ? "eager" : "lazy"}
            decoding="async"
          />
        </div>
        <div className="carousel__controls">
          <button
            type="button"
            className="carousel__btn"
            onClick={() => go(-1)}
            aria-label={g.prevLabel}
          >
            ‹
          </button>
          <div className="carousel__dots" role="tablist" aria-label={g.slidePickerLabel}>
            {slides.map((_, i) => (
              <button
                key={i}
                type="button"
                role="tab"
                aria-selected={i === index}
                className={`carousel__dot${i === index ? " is-active" : ""}`}
                onClick={() => setIndex(i)}
                aria-label={`${g.slideLabel} ${i + 1}`}
              />
            ))}
          </div>
          <button
            type="button"
            className="carousel__btn"
            onClick={() => go(1)}
            aria-label={g.nextLabel}
          >
            ›
          </button>
        </div>
        <p className="carousel__hint" aria-hidden="true">
          {g.hint}
        </p>
      </div>
    </Section>
  );
}
